/**
 * BY_FUEL_QUANTITY 前端分摊聚合纯函数。
 *
 * 设计依据：docs/70_explicit_fuel_allocation_design.md §4.4 / §5 / §6.3。
 * 本模块与后端 APAllocationService._handle_fuel_quantity_allocation 保持算法等价，
 * 供前端在用户审核阶段实时预览每航班权重与分摊金额；commit 时仍以后端计算为准。
 *
 * 聚合规则（三要素缺一不可，否则触发降级）：
 *   1. 仅聚合 DIRECT 行（RULE_BASED 行即使有 quantity 也不参与，避免自引用）；
 *   2. product_code 必须在 weight_source.product_codes 声明列表内（精确甄别油料行，排除固定费行）；
 *   3. quantity 必须为正数（排除 quantity=1 的占位、quantity=NULL 的缺失）。
 */

import { resolveLineItemProductCode, normalizeProductCode, isEmptyValue } from "./reviewPayloadSemantics";
import { roundMoney } from "./money";

const DEFAULT_WEIGHT_FIELD = "quantity";
const DEGRADED_TO = "BY_FLIGHT_COUNT";

const toFiniteNumber = (value) => {
    const numericValue = Number(value);
    return Number.isFinite(numericValue) ? numericValue : null;
};

/**
 * 从 line_item 上读取被分摊的金额（税费汇总行的总额）。
 * 优先取 total_price，兜底 unit_price*quantity。
 */
const resolveTotalAmount = (lineItem = {}) => {
    const total = toFiniteNumber(lineItem.total_price);
    if (total !== null) return total;
    const unitPrice = toFiniteNumber(lineItem.unit_price);
    const quantity = toFiniteNumber(lineItem.quantity);
    if (unitPrice !== null && quantity !== null) return roundMoney(unitPrice * quantity);
    return null;
};

/**
 * 计算按数量平均的分摊结果（降级用途，与 BY_FLIGHT_COUNT 等价）。
 */
const buildEvenAllocations = (flightIds, totalAmount) => {
    const count = flightIds.length;
    if (count === 0) return [];
    const safeTotal = totalAmount === null ? null : totalAmount;
    return flightIds.map((flightId, index) => {
        const weight = 1 / count;
        let amount = null;
        if (safeTotal !== null) {
            if (index === count - 1) {
                // 末条吸收尾差，保证总额精确闭合
                const allocated = roundMoney((safeTotal / count) * (count - 1));
                amount = roundMoney(safeTotal - allocated);
            } else {
                amount = roundMoney(safeTotal / count);
            }
        }
        return {
            flightId,
            weight,
            percentage: weight * 100,
            amount,
            aggregatedQuantity: null,
        };
    });
};

/**
 * 按 weight_source 在同发票内聚合各航班的油量权重，并计算分摊金额。
 *
 * @param {Object} lineItem        当前 B 类行（须含 weight_source、matched_flight_ids、total_price）
 * @param {Array}  invoiceLineItems 同发票全部 line_items
 * @returns {{
 *   degraded: boolean,
 *   degradedFrom?: string,
 *   degradedTo?: string,
 *   reason?: string,
 *   detail?: string,
 *   flightAllocations: Array<{flightId, weight, percentage, amount, aggregatedQuantity}>,
 *   totalAmount: number|null,
 *   totalQuantity: number,
 *   sourceItems: Array<{itemId: string|null, quantity: number, unitPrice: number|null, totalPrice: number|null, productCode: string}>,
 *   sourceItemIds: string[],
 *   weightSource: {productCodes: string[], field: string}|null,
 *   unit: string|null,
 * }}
 */
export const calculateFuelQuantityAllocation = (lineItem = {}, invoiceLineItems = []) => {
    const flightIds = Array.isArray(lineItem.matched_flight_ids)
        ? lineItem.matched_flight_ids.filter((id) => !isEmptyValue(id))
        : [];
    const totalAmount = resolveTotalAmount(lineItem);
    const weightSource = (lineItem.weight_source && typeof lineItem.weight_source === "object") ? lineItem.weight_source : null;
    const productCodesRaw = Array.isArray(weightSource?.product_codes) ? weightSource.product_codes : [];
    const field = !isEmptyValue(weightSource?.field) ? weightSource.field : DEFAULT_WEIGHT_FIELD;
    const declaredProductCodes = new Set(
        productCodesRaw.map(normalizeProductCode).filter((code) => code !== ""),
    );

    const buildDegraded = (reason, detail) => ({
        degraded: true,
        degradedFrom: "BY_FUEL_QUANTITY",
        degradedTo: DEGRADED_TO,
        reason,
        detail,
        flightAllocations: buildEvenAllocations(flightIds, totalAmount),
        totalAmount,
        totalQuantity: 0,
        sourceItems: [],
        sourceItemIds: [],
        weightSource: productCodesRaw.length > 0 ? { productCodes: productCodesRaw, field } : null,
    });

    // 降级条件 1：weight_source 缺失或 product_codes 为空
    if (!weightSource || declaredProductCodes.size === 0) {
        return buildDegraded("missing_weight_source", "缺少 weight_source 或未声明 product_codes，无法确定权重来源");
    }

    const lines = Array.isArray(invoiceLineItems) ? invoiceLineItems : [];

    // 聚合每航班的权重值 + 记录来源行
    const flightQtyMap = {};
    const sourceItems = [];
    let totalQuantity = 0;
    let aggregatedUnit = null;

    for (const li of lines) {
        if (li?.allocation_type !== "DIRECT") continue;
        const productCode = normalizeProductCode(resolveLineItemProductCode(li));
        if (!declaredProductCodes.has(productCode)) continue;
        const qty = toFiniteNumber(li?.[field]);
        if (qty === null || qty <= 0) continue;
        const matchedIds = Array.isArray(li.matched_flight_ids) ? li.matched_flight_ids : [];
        if (matchedIds.length === 0) continue;
        if (!aggregatedUnit && !isEmptyValue(li?.unit)) aggregatedUnit = li.unit;
        sourceItems.push({
            itemId: isEmptyValue(li?.item_id) ? null : li.item_id,
            quantity: qty,
            unitPrice: toFiniteNumber(li?.unit_price),
            totalPrice: resolveTotalAmount(li),
            productCode,
        });
        for (const fid of matchedIds) {
            if (isEmptyValue(fid)) continue;
            flightQtyMap[fid] = (flightQtyMap[fid] || 0) + qty;
        }
    }

    // 降级条件 2：无任何匹配的油料行
    const aggregatedFlightCount = Object.keys(flightQtyMap).length;
    if (aggregatedFlightCount === 0) {
        return buildDegraded(
            "no_matching_fuel_lines",
            `同发票内未找到 product_code 属于 [${productCodesRaw.join(", ")}] 且 quantity 为正数的 DIRECT 明细行`,
        );
    }

    // 降级条件 3：B 类行的 matched_flight_ids 部分缺失油量数据
    const missingFlights = flightIds.filter((fid) => !Object.prototype.hasOwnProperty.call(flightQtyMap, fid));
    if (missingFlights.length > 0) {
        return buildDegraded(
            "partial_fuel_coverage",
            `航班 [${missingFlights.join(", ")}] 在同发票内无对应 ${productCodesRaw.join("/")} 明细行`,
        );
    }

    // 正常分摊：按聚合油量加权
    for (const fid of flightIds) {
        totalQuantity += flightQtyMap[fid] || 0;
    }

    const flightAllocations = flightIds.map((flightId, index) => {
        const qty = flightQtyMap[flightId] || 0;
        const weight = totalQuantity > 0 ? qty / totalQuantity : 0;
        let amount = null;
        if (totalAmount !== null) {
            if (index === flightIds.length - 1) {
                // 末条吸收尾差，保证总额精确闭合
                const allocated = flightIds
                    .slice(0, -1)
                    .reduce((sum, fid) => sum + roundMoney(totalAmount * ((flightQtyMap[fid] || 0) / totalQuantity)), 0);
                amount = roundMoney(totalAmount - allocated);
            } else {
                amount = roundMoney(totalAmount * weight);
            }
        }
        return {
            flightId,
            weight,
            percentage: weight * 100,
            amount,
            aggregatedQuantity: roundMoney(qty),
        };
    });

    return {
        degraded: false,
        flightAllocations,
        totalAmount,
        totalQuantity: roundMoney(totalQuantity),
        sourceItems,
        sourceItemIds: sourceItems.map((s) => s.itemId).filter((id) => id !== null),
        weightSource: { productCodes: productCodesRaw, field },
        unit: aggregatedUnit,
    };
};

/**
 * 从同发票全部 line_items 中推断默认 weight_source.product_codes。
 * 当用户切换到 BY_FUEL_QUANTITY 且当前行无 weight_source 时调用：
 * 扫描同发票 DIRECT 行中出现频次最高的 product_code 作为默认值，
 * 若检测到 jet_fuel 类编码则优先采用。
 */
export const inferDefaultProductCodes = (invoiceLineItems = []) => {
    const lines = Array.isArray(invoiceLineItems) ? invoiceLineItems : [];
    const frequency = {};
    for (const li of lines) {
        if (li?.allocation_type !== "DIRECT") continue;
        const code = resolveLineItemProductCode(li);
        if (isEmptyValue(code)) continue;
        const normalized = normalizeProductCode(code);
        if (!normalized) continue;
        frequency[normalized] = (frequency[normalized] || 0) + 1;
    }
    const sorted = Object.entries(frequency).sort((a, b) => b[1] - a[1]);
    const fuelLike = sorted.find(([code]) => /fuel|jet/.test(code));
    if (fuelLike) return [fuelLike[0]];
    return sorted.length > 0 ? [sorted[0][0]] : [];
};

/**
 * 为前端展示构建分摊明细（按 allocation_type / allocation_rule 分派）。
 *
 * 供关联航班悬浮提示的「分摊信息」区块调用：给定当前 line_item 与同发票全部 line_items，
 * 返回统一的展示模型 { mode, rule, totalAmount, flightAllocations, ... }，由展示组件按 mode 渲染。
 *
 * mode 取值：
 *   - "DIRECT"：直接归集，无需分摊
 *   - "FUEL"：BY_FUEL_QUANTITY，复用 calculateFuelQuantityAllocation（含降级信息与油量聚合）
 *   - "EVEN"：其他 RULE_BASED 规则的前端估算（按数量平均），commit 仍以后端为准
 *   - "NONE"：RULE_BASED 但无关联航班，无法分摊
 */
export const buildAllocationBreakdown = (lineItem = {}, invoiceLineItems = []) => {
    const allocationType = lineItem.allocation_type;
    const allocationRule = lineItem.allocation_rule;
    const totalAmount = resolveTotalAmount(lineItem);
    const flightIds = Array.isArray(lineItem.matched_flight_ids)
        ? lineItem.matched_flight_ids.filter((id) => !isEmptyValue(id))
        : [];

    if (allocationType === "DIRECT") {
        return { mode: "DIRECT", rule: allocationRule, totalAmount, flightAllocations: [] };
    }

    if (flightIds.length === 0) {
        return { mode: "NONE", rule: allocationRule, totalAmount, flightAllocations: [] };
    }

    if (allocationRule === "BY_FUEL_QUANTITY") {
        const fuelResult = calculateFuelQuantityAllocation(lineItem, invoiceLineItems);
        return { mode: "FUEL", rule: allocationRule, totalAmount, ...fuelResult };
    }

    // 其他 RULE_BASED 规则：前端按数量平均展示估算（commit 仍以后端为准）
    return {
        mode: "EVEN",
        rule: allocationRule,
        totalAmount,
        flightAllocations: buildEvenAllocations(flightIds, totalAmount),
    };
};

export const ALLOCATION_DEGRADED_REASONS = {
    missing_weight_source: "缺少权重来源声明（weight_source 缺失或未指定 product_codes）",
    no_matching_fuel_lines: "同发票内无匹配的油料明细行",
    partial_fuel_coverage: "部分关联航班缺少油量数据",
};
